/* GRP UK header - sections/grp-header.liquid
   Mobile drawer open/close and touch/keyboard support for the department
   mega menus. Desktop mega menus open on hover/focus with CSS alone. */
(() => {
  const OPEN_CLASS = 'is-open';

  // The header section is sticky with its own stacking context, so move the
  // drawer to <body> to sit above the page, the cart drawer trigger bar and
  // the mobile bottom bar. Replaces a previous copy on theme-editor reloads.
  function mountDrawers(root = document) {
    root.querySelectorAll('.grp-header-section [data-grp-drawer]').forEach((drawer) => {
      const existing = document.body.querySelector(':scope > #' + CSS.escape(drawer.id));
      if (existing && existing !== drawer) existing.remove();
      document.body.appendChild(drawer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountDrawers(), { once: true });
  } else {
    mountDrawers();
  }
  document.addEventListener('shopify:section:load', (event) => mountDrawers(event.target));

  // Condensed sticky header: once the page scrolls past the header, hide the
  // department row and show the menu button (departments open in the drawer).
  const CONDENSE_AT = 160;
  const EXPAND_AT = 60;
  let condenseTicking = false;
  function updateCondensed() {
    condenseTicking = false;
    document.querySelectorAll('[data-grp-header][data-sticky="true"]').forEach((header) => {
      const condensed = header.classList.contains('is-condensed');
      if (!condensed && window.scrollY > CONDENSE_AT) header.classList.add('is-condensed');
      else if (condensed && window.scrollY < EXPAND_AT) header.classList.remove('is-condensed');
    });
  }
  window.addEventListener('scroll', () => {
    if (condenseTicking) return;
    condenseTicking = true;
    requestAnimationFrame(updateCondensed);
  }, { passive: true });

  // Phones: shrinking header. Scrolling down (past a threshold, with a little travel so it never flickers) hides the
  // search row and shrinks the logo; scrolling up, or being near the top, brings it back. A margin on the sticky
  // wrapper cancels the height change, so the page content never moves. Search stays one tap away through the
  // header's own search icon (it opens the theme's predictive search, which is left alone).
  const compactQuery = window.matchMedia('(max-width: 767px)');
  const COMPACT_AFTER = 120;    // px scrolled down before the header shrinks (once)
  const EXPAND_BELOW = 24;      // it only comes back when the visitor is back at the very top
  let compactHeader = null;
  let compactState = false;
  let compactTicking = false;
  let lastWidth = window.innerWidth;

  function setCompact(on) {
    if (!compactHeader || compactState === on) return;
    compactState = on;
    compactHeader.classList.toggle('is-compact', on);
    document.documentElement.classList.toggle('frk-header-compact', on);
  }

  // Heights of the two states, measured with transitions off so nothing visibly flickers. The search row's own
  // height is measured too, so its fold animates over exactly the distance it covers (no dead zone at the start).
  function measureCompact() {
    if (!compactHeader) return;
    const root = document.documentElement;
    const was = compactState;
    root.classList.add('frk-header-measuring');
    compactHeader.classList.remove('is-compact');
    const full = compactHeader.offsetHeight;
    const search = compactHeader.querySelector('.grp-search');
    if (search) root.style.setProperty('--frk-search-h', search.scrollHeight + 'px');
    compactHeader.classList.add('is-compact');
    const small = compactHeader.offsetHeight;
    if (!was) compactHeader.classList.remove('is-compact');
    // Settle the restored state while transitions are still off; otherwise the browser animates from the
    // measured compact height back to full height and the whole page bounces 58px after load.
    void compactHeader.offsetHeight;
    root.classList.remove('frk-header-measuring');
    root.style.setProperty('--frk-header-delta', Math.max(0, full - small) + 'px');
    root.style.setProperty('--frk-header-full-h', full + 'px');
    root.style.setProperty('--frk-header-compact-h', small + 'px');
  }

  // One state, decided by position only (never by scroll direction): shrink past COMPACT_AFTER, restore at the top.
  // Between the two it keeps whatever it is, so scrolling up mid-page, bounces and the browser's address bar
  // growing or shrinking cannot make it flip.
  function updateCompact() {
    compactTicking = false;
    if (!compactHeader) return;
    if (!compactQuery.matches) { setCompact(false); return; }
    // Never change state while a drawer / popup has locked the page, or while the header itself has focus (search)
    if (document.documentElement.classList.contains('prevent-scroll') || compactHeader.matches(':focus-within')) return;
    const y = Math.max(0, window.scrollY);
    if (!compactState && y > COMPACT_AFTER) setCompact(true);
    else if (compactState && y <= EXPAND_BELOW) setCompact(false);
  }

  function initCompactHeader() {
    compactHeader = document.querySelector('[data-grp-header][data-sticky="true"]');
    if (!compactHeader) return;
    measureCompact();
    // First pass (e.g. page reloaded part-way down) applies instantly rather than animating
    const root = document.documentElement;
    root.classList.add('frk-header-measuring');
    updateCompact();
    void compactHeader.offsetHeight;
    root.classList.remove('frk-header-measuring');
  }

  window.addEventListener('scroll', () => {
    if (compactTicking) return;
    compactTicking = true;
    requestAnimationFrame(updateCompact);
  }, { passive: true });
  let compactResize = 0;
  window.addEventListener('resize', () => {
    // Height-only resizes are the mobile browser's toolbar moving; the header does not care about those
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    window.clearTimeout(compactResize);
    compactResize = window.setTimeout(() => { if (!compactQuery.matches) setCompact(false); measureCompact(); }, 150);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCompactHeader, { once: true });
  else initCompactHeader();
  window.addEventListener('load', measureCompact, { once: true });   // logo / fonts settled
  document.addEventListener('shopify:section:load', initCompactHeader);

  // Cart subtotal beside the cart icon. The theme updates the cart count
  // bubble after every cart change; refresh the subtotal when it changes.
  let subtotalTimer = null;
  function refreshSubtotal() {
    window.clearTimeout(subtotalTimer);
    subtotalTimer = window.setTimeout(() => {
      fetch((window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          document.querySelectorAll('[data-grp-cart-subtotal]').forEach((el) => {
            el.setAttribute('data-price-cents', cart.total_price);
          });
          if (window.__VAT && window.__VAT.applyVatToAllPrices) window.__VAT.applyVatToAllPrices();
        })
        .catch(() => {});
    }, 150);
  }

  function watchCartCount() {
    document.querySelectorAll('[data-grp-cart] .m-cart-count').forEach((count) => {
      if (count.__grpWatched) return;
      count.__grpWatched = true;
      new MutationObserver(refreshSubtotal).observe(count, { childList: true, characterData: true, subtree: true, attributes: true });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCartCount, { once: true });
  } else {
    watchCartCount();
  }
  document.addEventListener('shopify:section:load', watchCartCount);
  document.addEventListener('cart:refresh', refreshSubtotal);
  document.addEventListener('cart:updated', refreshSubtotal);

  function openDrawer(drawer, opener) {
    drawer.hidden = false;
    drawer.__opener = opener;
    requestAnimationFrame(() => drawer.classList.add(OPEN_CLASS));
    document.documentElement.classList.add('prevent-scroll');
    if (opener) opener.setAttribute('aria-expanded', 'true');
    const close = drawer.querySelector('.grp-drawer__head [data-grp-drawer-close]');
    if (close) close.focus();
  }

  function closeDrawer(drawer) {
    drawer.classList.remove(OPEN_CLASS);
    document.documentElement.classList.remove('prevent-scroll');
    if (drawer.__opener) {
      drawer.__opener.setAttribute('aria-expanded', 'false');
      drawer.__opener.focus();
    }
    window.setTimeout(() => { drawer.hidden = true; }, 240);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-grp-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.getAttribute('aria-controls'));
      if (drawer) openDrawer(drawer, opener);
      return;
    }

    const cartTotal = event.target.closest('[data-grp-cart-open]');
    if (cartTotal) {
      const cartLink = cartTotal.closest('[data-grp-cart]').querySelector('.m-cart-icon-bubble');
      if (cartLink) cartLink.click();
      return;
    }

    const closer = event.target.closest('[data-grp-drawer-close]');
    if (closer) {
      const drawer = closer.closest('[data-grp-drawer]');
      if (drawer) closeDrawer(drawer);
      return;
    }

    // Touch screens: first tap on a department with a mega menu opens it,
    // a second tap follows the link.
    const navLink = event.target.closest('.grp-nav__item--mega > .grp-nav__link');
    const isTouch = window.matchMedia('(hover: none)').matches;
    if (navLink && isTouch) {
      const item = navLink.parentElement;
      if (!item.classList.contains(OPEN_CLASS)) {
        event.preventDefault();
        document.querySelectorAll('.grp-nav__item.' + OPEN_CLASS).forEach((el) => el.classList.remove(OPEN_CLASS));
        item.classList.add(OPEN_CLASS);
      }
      return;
    }

    if (!event.target.closest('.grp-nav')) {
      document.querySelectorAll('.grp-nav__item.' + OPEN_CLASS).forEach((el) => el.classList.remove(OPEN_CLASS));
    }
  });

  // Images inside the mega menus and the search popup are written without a
  // src (data-grp-src, see snippets/grp-asset-img.liquid) so they don't download
  // on every page. They load the first time the menu or popup is about to open.
  function loadDeferred(scope) {
    if (!scope || scope.__grpImgsLoaded) return;
    scope.__grpImgsLoaded = true;
    scope.querySelectorAll('img[data-grp-src]').forEach((img) => {
      if (img.dataset.grpSrcset) img.srcset = img.dataset.grpSrcset;
      img.src = img.dataset.grpSrc;
      img.removeAttribute('data-grp-src');
      img.removeAttribute('data-grp-srcset');
    });
  }

  const searchPopup = () => document.querySelector('[data-search-popup]');
  const warmUp = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const item = target.closest('.grp-nav__item--mega');
    if (item) loadDeferred(item);
    if (target.closest('[data-open-search-popup]')) loadDeferred(searchPopup());
  };
  document.addEventListener('pointerover', warmUp, { passive: true });
  document.addEventListener('focusin', warmUp);
  document.addEventListener('touchstart', warmUp, { passive: true });

  // Fallback: the popup can also be opened by other triggers (bottom bar, shortcuts)
  function watchSearchPopup() {
    const container = document.querySelector('[data-search-container]');
    if (!container || container.__grpWatched) return;
    container.__grpWatched = true;
    new MutationObserver(() => {
      if (container.classList.contains('m-show-search')) loadDeferred(searchPopup());
    }).observe(container, { attributes: true, attributeFilter: ['class'] });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchSearchPopup, { once: true });
  } else {
    watchSearchPopup();
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const drawer = document.querySelector('[data-grp-drawer].' + OPEN_CLASS);
    if (drawer) {
      closeDrawer(drawer);
      return;
    }
    const focusedItem = document.activeElement && document.activeElement.closest('.grp-nav__item--mega');
    if (focusedItem) {
      focusedItem.querySelector('.grp-nav__link').focus();
      focusedItem.classList.remove(OPEN_CLASS);
      document.activeElement.blur();
    }
  });
})();
